/**
 * Status enums used across the compliance module.
 *
 * ITEM_STATUSES — workflow order for compliance items.
 * DATA_STATUSES — state of source data (for items that depend on external docs).
 * PERIOD_BUCKETS — date-derived bucket labels used for calendar + filters.
 * SERVICE_STATUSES — for per-jurisdiction service records (engagement-scoped).
 *
 * Statuses are intentionally string-typed and frozen as `as const` so they
 * double as Zod enum values and React badge labels.
 */

export const ITEM_STATUSES = [
  'Pending',
  'InProcess',
  'ReadyForReview',
  'Reviewed',
  'SentBack',
  'SentToClient',
  'Completed',
] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];

export const DATA_STATUSES = [
  'DataReceived',
  'PendingRequest',
  'DataRequested',
] as const;
export type DataStatus = (typeof DATA_STATUSES)[number];

export const PERIOD_BUCKETS = [
  'Overdue',
  'Due Today',
  'Due This Week',
  'Due This Month',
  'Due After 30 Days',
  'Completed',
] as const;
export type PeriodBucket = (typeof PERIOD_BUCKETS)[number];

export const SERVICE_STATUSES = [
  'PENDING',
  'IN_PROGRESS',
  'COMPLETED',
  'BLOCKED',
  'CANCELLED',
] as const;
export type ServiceStatus = (typeof SERVICE_STATUSES)[number];

/** Staff-only statuses (assignee1+ can mark these). */
export const STAFF_ITEM_STATUSES: readonly ItemStatus[] = [
  'Pending',
  'InProcess',
  'ReadyForReview',
] as const;

/** Reviewer-only action statuses (assignee1/Manager+). */
export const REVIEW_ITEM_STATUSES: readonly ItemStatus[] = [
  'Reviewed',
  'SentBack',
  'SentToClient',
  'Completed',
] as const;

/** Maximum times an item can bounce before auto-escalating to manager. */
export const SENT_BACK_LIMIT = 2;

/** Status colour classes — semantic pill classes defined in web's index.css. */
export const STATUS_BADGE_CLASSES: Record<ItemStatus, string> = {
  'Pending':          'pill-neutral',
  'InProcess':        'pill-info',
  'ReadyForReview':   'pill-warn',
  'Reviewed':         'pill-indigo',
  'SentBack':         'pill-danger',
  'SentToClient':     'pill-teal',
  'Completed':        'pill-success',
};

export const DATA_STATUS_BADGE_CLASSES: Record<DataStatus, string> = {
  'DataReceived':    'pill-success',
  'PendingRequest':  'pill-warn',
  'DataRequested':   'pill-neutral',
};

export const PERIOD_BADGE_CLASSES: Record<PeriodBucket, string> = {
  'Overdue':           'pill-danger',
  'Due Today':         'pill-warn',
  'Due This Week':     'pill-warn',
  'Due This Month':    'pill-info',
  'Due After 30 Days': 'pill-neutral',
  'Completed':         'pill-success',
};

/** Human-readable labels for Prisma-mapped enum values. */
export const ITEM_STATUS_LABELS: Record<ItemStatus, string> = {
  'Pending':          'Pending',
  'InProcess':        'In Process',
  'ReadyForReview':   'Ready for review',
  'Reviewed':         'Reviewed',
  'SentBack':         'Sent Back',
  'SentToClient':     'Sent to Client',
  'Completed':        'Completed',
};

export const DATA_STATUS_LABELS: Record<DataStatus, string> = {
  'DataReceived':   'Data Received',
  'PendingRequest': 'Pending Request',
  'DataRequested':  'Data Requested',
};


/** Engagement pipeline stage labels. OPS 3.5: stages cut to Lead → Proposal
 *  Sent → Won / Lost (CONTACTED relabelled Lead, ENGAGED → Won,
 *  NOT_INTERESTED → Lost; QUALIFIED/NEED_ANALYSIS/NEGOTIATION removed).
 *  OPS 3.5 B: NEGOTIATION restored — Lead → Proposal Sent → Negotiation →
 *  Won / Lost; DEFERRED stays in the enum (existing rows) but off the visible
 *  pipeline. */
export const ENGAGEMENT_PIPELINE_LABELS: Record<string, string> = {
  'CONTACTED': 'Lead',
  'PROPOSAL_SENT': 'Proposal Sent',
  'NEGOTIATION': 'Negotiation',
  'ENGAGED': 'Won',
  'DEFERRED': 'Deferred',
  'NOT_INTERESTED': 'Lost',
};

/** Pipeline stages shown in the engagement UI dropdowns/cards.
 *  DEFERRED and NOT_INTERESTED are terminal and set by automation/kick-off, not user-selectable. */
export const ENGAGEMENT_PIPELINE_VISIBLE_STAGES = [
  'CONTACTED',
  'PROPOSAL_SENT',
  'NEGOTIATION',
  'ENGAGED',
] as const;

/** Proposal status labels. OPS 3.5 B: SENT_BACK added — the client used the
 *  public negotiate flow and asked for changes; staff edits the proposal
 *  (back to DRAFT) and re-sends. */
export const PROPOSAL_STATUS_LABELS: Record<string, string> = {
  'DRAFT': 'Draft',
  'SENT': 'Sent',
  'VIEWED': 'Viewed',
  'SENT_BACK': 'Sent Back',
  'SIGNED': 'Signed',
  'DECLINED': 'Declined',
  'EXPIRED': 'Expired',
};

/** Engagement type labels. */
export const ENGAGEMENT_TYPE_LABELS: Record<string, string> = {
  'ONE_TIME': 'One-time',
  'RETAINER': 'Retainer',
  'ANNUAL': 'Annual',
};

/** Engagement interest labels. OPS 3.7 step 2: the field is dead on the web
 *  (engagement IS business setup; new rows default BUSINESS_SETUP) — labels
 *  kept for the installed mobile app + legacy rows. */
export const ENGAGEMENT_INTEREST_LABELS: Record<string, string> = {
  'BUSINESS_SETUP': 'Business Setup',
  'ACCOUNTING': 'Accounting',
  'TAXATION': 'Taxation',
  'AUDIT': 'Audit',
  'ADVISORY': 'Advisory',
  'FP_AND_A': 'FP&A',
  'PAYROLL': 'Payroll',
  'CORPORATE': 'Corporate',
};

/** OPS 3.7 step 2: execution-checklist sub-item status labels. */
export const ENGAGEMENT_SUB_ITEM_STATUS_LABELS: Record<string, string> = {
  'PENDING': 'Pending',
  'IN_PROCESS': 'In Process',
  'COMPLETED': 'Completed',
};

/** Open (pre-signature) engagement pipeline stages — the composer lane check
 *  treats an engagement in one of these as "open" (Lane B setup work). */
export const ENGAGEMENT_OPEN_STAGES: readonly string[] = ['CONTACTED', 'PROPOSAL_SENT', 'NEGOTIATION'];
